import { searchProviderLabel } from '../../../api/src/search/search-provider.types';
import { EditorOnly, useCanEdit } from '../components/EditorOnly';
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TablePagination,
  TextField,
} from '@mui/material';
import { Box, Stack, Typography } from '../components/mui-system';
import { api } from '../api/client';
import type { TelegramChannel, FakesSearchProfile } from '../api/types';
import type {
  FakesReportSettingsInput,
  FakesReportSettingsView,
  FakesReportView,
} from '../../../api/src/fakes/fakes-reports.types';
import { fakeDate, fakeError } from '../components/fakes-presentation';
import { DEFAULT_SERP_MAP_STYLE } from '../../../api/src/fakes/serp-appearance';
import { SerpMapStyleSelect } from '../components/SerpMapStyleSelect';
const config = (s: FakesReportSettingsView): FakesReportSettingsInput => ({
  revision: s.revision,
  enabled: s.enabled,
  top: s.top,
  mapStyle: s.mapStyle ?? DEFAULT_SERP_MAP_STYLE,
  destinations: s.destinations,
});
const statusLabels = {
  waiting: 'Ожидает отчёта',
  pending: 'В очереди',
  sending: 'Отправляется',
  retrying: 'Ожидает повтора',
  sent: 'Доставлено',
  failed: 'Ошибка доставки',
  unknown: 'Доставка неизвестна',
  cancelled: 'Отменено',
};
interface ReportsDialogProps {
  workspace: string;
  groupId?: string;
  groupName?: string;
  groups?: Array<{ id: string; name: string; count: number }>;
  count: number;
  profile: FakesSearchProfile | null;
  onClose: () => void;
}
export function FakesReportsDialog(props: ReportsDialogProps) {
  const [scope, setScope] = useState(props.groupId ?? props.workspace);
  const groups = props.groups ?? [
    {
      id: props.groupId ?? props.workspace,
      name: props.groupName ?? 'Основная',
      count: props.count,
    },
  ];
  return (
    <ScopedFakesReportsDialog
      {...props}
      key={scope}
      groups={groups}
      groupId={scope}
      groupName={scope === 'all' ? 'Все группы' : groups.find((g) => g.id === scope)?.name}
      count={
        scope === 'all'
          ? groups.reduce((sum, g) => sum + g.count, 0)
          : (groups.find((g) => g.id === scope)?.count ?? 0)
      }
      onScopeChange={setScope}
    />
  );
}
function ScopedFakesReportsDialog({
  workspace,
  groupId = workspace,
  groupName,
  groups,
  onScopeChange,
  count,
  onClose,
}: ReportsDialogProps & { onScopeChange: (id: string) => void }) {
  const canViewTelegram = useCanEdit('telegram.view');
  const editable = useCanEdit('fakes.settings', 'fakes.schedule', 'fakes.send');
  const [saved, setSaved] = useState<FakesReportSettingsView | null>(null),
    [draft, setDraft] = useState<FakesReportSettingsInput | null>(null);
  const [channels, setChannels] = useState<TelegramChannel[]>([]),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false),
    [confirmation, setConfirmation] = useState(false),
    [page, setPage] = useState(0);
  const [history, setHistory] = useState<{ items: FakesReportView[]; total: number } | null>(null);
  const [preview, setPreview] = useState<{
    html: string;
    caption: string;
    imageBase64?: string | null;
    filename?: string;
  } | null>(null);
  const [connection, setConnection] = useState<{
    title: string;
    chatId: string;
    botToken: string;
  } | null>(null);
  const live = useRef(true),
    locked = useRef(false),
    generation = useRef(0),
    settingsGeneration = useRef(0);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      generation.current++;
      settingsGeneration.current++;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api.fakesReportSettings(workspace, groupId),
      canViewTelegram ? api.telegram(workspace) : Promise.resolve([]),
    ])
      .then(([s, c]) => {
        if (!cancelled) {
          setSaved(s);
          setDraft(config(s));
          setChannels(c);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(fakeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [workspace, canViewTelegram, groupId]);
  useEffect(() => {
    let cancelled = false,
      pending = false;
    async function refresh() {
      if (pending || locked.current) return;
      pending = true;
      const request = ++generation.current,
        settingsRequest = ++settingsGeneration.current;
      try {
        const [s, h] = await Promise.all([
          api.fakesReportSettings(workspace, groupId),
          api.fakesReports(workspace, page + 1, groupId),
        ]);
        if (
          cancelled ||
          !live.current ||
          request !== generation.current ||
          settingsRequest !== settingsGeneration.current
        )
          return;
        setHistory(h);
        setSaved((previous) =>
          previous
            ? {
                ...previous,
                nextCollectAt: s.nextCollectAt,
                recent: s.recent,
                collectTime: s.collectTime,
                timeZone: s.timeZone,
                collectionEnabled: s.collectionEnabled,
              }
            : previous,
        );
      } catch (e) {
        if (!cancelled && live.current) setError(fakeError(e));
      } finally {
        pending = false;
      }
    }
    void refresh();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [workspace, groupId, page]);
  const dirty = Boolean(saved && draft && JSON.stringify(config(saved)) !== JSON.stringify(draft));
  async function act(action: () => Promise<void>) {
    if (locked.current) return;
    locked.current = true;
    settingsGeneration.current++;
    generation.current++;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
    } catch (e) {
      if (live.current) setError(fakeError(e));
    } finally {
      locked.current = false;
      if (live.current) setBusy(false);
    }
  }
  async function save() {
    if (!draft) return;
    await act(async () => {
      const s = await api.saveFakesReportSettings(workspace, draft, groupId);
      if (!live.current) return;
      setSaved(s);
      setDraft(config(s));
      setConfirmation(false);
      setNotice('Настройки сохранены.');
    });
  }
  async function openPreview(id?: string) {
    await act(async () => {
      const p = id
        ? await api.fakesReportDocument(workspace, id)
        : await api.fakesReportPreview(workspace, groupId);
      if (live.current) setPreview(p);
    });
  }
  const sendPending = saved?.recent.some(
    (r) =>
      r.kind === 'manual' &&
      (r.phase === 'collecting' ||
        r.deliveries.some((d) => ['waiting', 'pending', 'sending', 'retrying'].includes(d.status))),
  );
  function download(format: 'html' | 'png') {
    if (!preview) return;
    const data =
      format === 'png'
        ? preview.imageBase64
          ? Uint8Array.from(atob(preview.imageBase64), (c) => c.charCodeAt(0))
          : null
        : preview.html;
    if (data === null) return;
    const url = URL.createObjectURL(
      new Blob([data], { type: format === 'png' ? 'image/png' : 'text/html;charset=utf-8' }),
    );
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download =
      format === 'png' ? 'serp-demand-map.png' : (preview.filename ?? 'fakes-preview.html');
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <>
      <Dialog open fullWidth maxWidth="md" onClose={() => !busy && onClose()}>
        <DialogTitle>Ежедневные отчёты · {groupName ?? 'Карта выдачи'}</DialogTitle>
        {(error || notice) && (
          <Stack gap={1} mx={2} mb={1}>
            {error && <Alert severity="error">{error}</Alert>}
            {notice && <Alert severity="success">{notice}</Alert>}
          </Stack>
        )}
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              select
              label="Состав отчёта"
              value={groupId}
              disabled={busy || dirty}
              helperText={
                dirty
                  ? 'Сохраните настройки перед сменой состава отчёта.'
                  : 'У общего отчёта и каждой группы свои получатели и настройки отправки.'
              }
              onChange={(e) => onScopeChange(e.target.value)}
            >
              <MenuItem value="all">Все группы · общий отчёт</MenuItem>
              {groups?.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </TextField>
            {!saved || !draft ? (
              !error && <CircularProgress aria-label="Загрузка отчётности" />
            ) : (
              <>
                <Typography color="text.secondary">
                  {groupId === 'all'
                    ? 'Общий отчёт охватывает все группы.'
                    : `Отчёт по группе «${groupName}».`}{' '}
                  Использует сохранённый дневной сбор: одним сообщением в Telegram — сводка,
                  PNG-карта и HTML с разбором, даже если изменений нет.
                </Typography>
                <FormControlLabel
                  label="Отправлять отчёт после дневного сбора"
                  control={
                    <Switch
                      checked={draft.enabled}
                      disabled={!editable || busy}
                      onChange={(_, enabled) => setDraft({ ...draft, enabled })}
                    />
                  }
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <TextField
                    label="Top-N в отчёте"
                    sx={{ width: { xs: '100%', sm: 150 }, flexShrink: 0 }}
                    type="number"
                    value={draft.top}
                    disabled={!editable || busy}
                    slotProps={{ htmlInput: { min: 1, max: 100 } }}
                    onChange={(e) => setDraft({ ...draft, top: Number(e.target.value) })}
                  />
                  <SerpMapStyleSelect
                    value={draft.mapStyle ?? DEFAULT_SERP_MAP_STYLE}
                    disabled={!editable || busy}
                    onChange={(mapStyle) => setDraft({ ...draft, mapStyle })}
                  />
                </Stack>
                {!saved.collectionEnabled && (
                  <Alert severity="warning">
                    Дневной сбор выдачи выключен. Включите его через «Расписание» в Карте выдачи;
                    отправка сегодняшнего сохранённого отчёта доступна отдельно.
                  </Alert>
                )}
                <Alert severity="info">
                  Активных запросов в отчёте: {count}.{' '}
                  {groupId === 'all' ? (
                    'Профили и время сбора задаются в каждой группе. Общий дневной отчёт ждёт сборы групп с включённым расписанием.'
                  ) : (
                    <>
                      {saved.profile
                        ? `${searchProviderLabel(saved.profile)} · ${saved.profile.searchEngine === 'yandex' ? 'Яндекс' : 'Google'} · ${saved.profile.regionName} · сбор Top-${saved.profile.depth}.`
                        : 'Профиль группы ещё не выбран.'}{' '}
                      Расписание сбора: {saved.collectTime} ({saved.timeZone}). Оно настраивается
                      кнопкой «Расписание» выбранной группы.
                    </>
                  )}{' '}
                  Top-N отчёта ограничен глубиной профиля.
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  Отправка начинается после завершения сбора. Через 6 часов ожидания придёт неполный
                  отчёт с пояснением. Выключение отчётности отменяет ещё не начатые отправки; уже
                  запущенный сбор продолжит работу.
                </Typography>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h3">Получатели Telegram</Typography>
                  <EditorOnly permissions="telegram.settings">
                    <Button
                      disabled={busy || !saved.encryptionReady}
                      onClick={() => setConnection({ title: '', chatId: '', botToken: '' })}
                    >
                      Новое подключение
                    </Button>
                  </EditorOnly>
                </Stack>
                {!saved.encryptionReady && (
                  <Alert severity="warning">
                    На сервере не настроено шифрование токенов. Нужен APP_ENCRYPTION_KEY.
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary">
                  Можно отправить один отчёт в несколько чатов или тем — например, SEO и ИБ.
                  Подключения общие с разделом Telegram.
                </Typography>
                {draft.destinations.map((d, i) => (
                  <Stack key={i} direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems="start">
                    <TextField
                      fullWidth
                      select
                      label={`Получатель ${i + 1}`}
                      value={d.channelId}
                      disabled={!editable || busy}
                      slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          destinations: draft.destinations.map((v, n) =>
                            n === i ? { ...v, channelId: e.target.value } : v,
                          ),
                        })
                      }
                    >
                      <MenuItem value="">Выберите подключение</MenuItem>
                      {channels.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.title} · {c.chatId}
                          {c.enabled && c.hasBotToken ? '' : ' · не готово'}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label={`ID темы ${i + 1}`}
                      type="number"
                      value={d.topicId ?? ''}
                      disabled={!editable || busy}
                      helperText="Необязательно"
                      slotProps={{ htmlInput: { min: 1, max: 2147483647 } }}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          destinations: draft.destinations.map((v, n) =>
                            n === i
                              ? { ...v, topicId: e.target.value ? Number(e.target.value) : null }
                              : v,
                          ),
                        })
                      }
                    />
                    <EditorOnly permissions={['fakes.settings', 'fakes.schedule', 'fakes.send']}>
                      <Button
                        color="inherit"
                        disabled={busy}
                        aria-label={`Убрать получателя ${i + 1}`}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            destinations: draft.destinations.filter((_, n) => i !== n),
                          })
                        }
                      >
                        Убрать
                      </Button>
                    </EditorOnly>
                  </Stack>
                ))}
                <EditorOnly permissions={['fakes.settings', 'fakes.schedule', 'fakes.send']}>
                  <Button
                    disabled={busy || draft.destinations.length >= 10}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        destinations: [...draft.destinations, { channelId: '', topicId: null }],
                      })
                    }
                  >
                    Добавить получателя
                  </Button>
                </EditorOnly>
                <Divider />
                <Typography variant="h3">Содержание отчёта</Typography>
                <Typography variant="body2">
                  PNG: карта спроса и запросы, требующие внимания. HTML: карта позиций → важные
                  ухудшения → приоритетные запросы → улучшения → обзор групп. Сравниваются только
                  запросы с полными данными в обоих сборах. Миникарты, ссылки и основания решений
                  раскрываются внутри карточек. Полный список — в дашборде.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                  <Button
                    variant="outlined"
                    disabled={busy || dirty}
                    onClick={() => void openPreview()}
                  >
                    Предпросмотр по текущим данным
                  </Button>
                  <EditorOnly permissions="fakes.send">
                    <Button
                      variant="outlined"
                      disabled={
                        busy ||
                        dirty ||
                        !saved.revision ||
                        !saved.destinations.length ||
                        !saved.encryptionReady ||
                        Boolean(sendPending)
                      }
                      onClick={() =>
                        void act(async () => {
                          const s = await api.sendTodayFakesReport(
                            workspace,
                            saved.revision,
                            groupId,
                          );
                          if (live.current) {
                            setSaved(s);
                            setNotice(
                              'Сегодняшний отчёт поставлен в очередь Telegram. Статусы каждого чата и темы — в истории ниже.',
                            );
                            const h = await api.fakesReports(workspace, 1, groupId);
                            if (!live.current) return;
                            setPage(0);
                            setHistory(h);
                          }
                        })
                      }
                    >
                      Отправить сегодняшний отчёт
                    </Button>
                  </EditorOnly>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Отправка использует последний запуск за сегодня после завершения сбора (
                  {saved.timeZone}) и сохранённых получателей выше. Новый сбор выдачи не
                  запускается. Можно проверить бота, chat ID и ID темы даже при выключенном
                  расписании.
                </Typography>
                {dirty && (
                  <Typography fontSize={12} color="text.secondary">
                    Сначала сохраните изменения. Предпросмотр и отправка используют сохранённые
                    настройки.
                  </Typography>
                )}
                <Divider />
                <Typography variant="h3">История и доставка</Typography>
                <Typography variant="body2">
                  Следующий сбор:{' '}
                  {saved.nextCollectAt
                    ? `${new Date(saved.nextCollectAt).toLocaleString('ru-RU', { timeZone: saved.timeZone })} (${saved.timeZone})`
                    : 'расписание выключено'}
                  .
                </Typography>
                {!history ? (
                  <CircularProgress size={20} aria-label="Загрузка истории отчётов" />
                ) : !history.items.length ? (
                  <Typography color="text.secondary">Отчётов пока нет.</Typography>
                ) : (
                  history.items.map((r) => (
                    <Card key={r.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack gap={1}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Typography fontWeight={700}>
                            {r.kind === 'test'
                              ? 'Тест доставки (старый формат)'
                              : r.kind === 'manual'
                                ? `${r.day} · Ручная отправка`
                                : r.day}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              r.phase === 'collecting'
                                ? r.kind === 'manual'
                                  ? 'Подготовка отчёта'
                                  : 'Сбор и подготовка'
                                : r.phase === 'ready'
                                  ? 'Отчёт готов'
                                  : 'Отменён'
                            }
                          />
                        </Stack>
                        <Typography fontSize={12} color="text.secondary">
                          Создан {fakeDate(r.createdAt)}
                          {r.preparedAt ? ` · подготовлен ${fakeDate(r.preparedAt)}` : ''}
                        </Typography>
                        {r.error && <Alert severity="warning">{r.error}</Alert>}
                        {r.deliveries.map((d) => (
                          <Typography key={d.id} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                            {d.part === 'report'
                              ? 'Отчёт · '
                              : d.part === 'photo'
                                ? 'Картинка · '
                                : 'HTML · '}
                            {d.chatId || 'Получатель удалён'}
                            {d.topicId ? ` · тема ${d.topicId}` : ''} — {statusLabels[d.status]}
                            {d.sentAt ? ` · ${fakeDate(d.sentAt)}` : ''}
                            {d.error ? ` · ${d.error}` : ''}
                          </Typography>
                        ))}
                        {r.hasDocument && (
                          <EditorOnly permissions="fakes.export">
                            <Button
                              sx={{ alignSelf: 'start' }}
                              disabled={busy}
                              onClick={() => void openPreview(r.id)}
                            >
                              Открыть сохранённый отчёт
                            </Button>
                          </EditorOnly>
                        )}
                      </Stack>
                    </Card>
                  ))
                )}
                <TablePagination
                  component="div"
                  count={history?.total ?? 0}
                  page={page}
                  rowsPerPage={10}
                  rowsPerPageOptions={[10]}
                  onPageChange={(_, p) => setPage(p)}
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={onClose}>
            Закрыть
          </Button>
          <EditorOnly permissions={['fakes.settings', 'fakes.schedule', 'fakes.send']}>
            <Button
              variant="contained"
              disabled={busy || !draft || (!dirty && saved?.revision !== 0)}
              onClick={() => (draft?.enabled ? setConfirmation(true) : void save())}
            >
              Сохранить настройки
            </Button>
          </EditorOnly>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmation}
        onClose={() => !busy && setConfirmation(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Включить ежедневный отчёт?</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography>
              Отчёт будет подготовлен после дневного сбора выдачи. Это действие включает только
              отправку; расписания выдачи и n8n не меняются.
            </Typography>
            <Typography>
              Сводка, PNG-карта и HTML-отчёт будут отправлены одним сообщением получателям:{' '}
              {draft?.destinations
                .map(
                  (d) =>
                    `${channels.find((c) => c.id === d.channelId)?.chatId ?? 'не выбран'}${d.topicId ? ' · тема ' + d.topicId : ''}`,
                )
                .join('; ')}
              .
            </Typography>
            <Typography variant="body2">
              Если дневной сбор уже выполнен, а сегодняшний отчёт ещё не создавался, он будет
              подготовлен в ближайшую минуту.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirmation(false)}>
            Отмена
          </Button>
          <EditorOnly permissions={['fakes.settings', 'fakes.schedule', 'fakes.send']}>
            <Button disabled={busy} variant="contained" onClick={() => void save()}>
              Подтвердить расписание
            </Button>
          </EditorOnly>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(connection)}
        onClose={() => !busy && setConnection(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Новое Telegram-подключение</DialogTitle>
        <DialogContent>
          {connection && (
            <Stack gap={2} mt={1}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                disabled={!editable}
                label="Название подключения"
                value={connection.title}
                onChange={(e) => setConnection({ ...connection, title: e.target.value })}
              />
              <TextField
                disabled={!editable}
                label="Chat ID"
                value={connection.chatId}
                onChange={(e) => setConnection({ ...connection, chatId: e.target.value })}
              />
              <TextField
                disabled={!editable}
                label="Токен бота"
                type="password"
                autoComplete="new-password"
                value={connection.botToken}
                onChange={(e) => setConnection({ ...connection, botToken: e.target.value })}
              />
              <Typography variant="body2">
                Токен шифруется на сервере. Сохранение подключения не отправляет сообщение.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConnection(null)}>
            Отмена
          </Button>
          <EditorOnly permissions="telegram.settings">
            <Button
              disabled={busy || !connection?.title || !connection.chatId || !connection.botToken}
              onClick={() =>
                void act(async () => {
                  if (!connection) return;
                  const c = await api.saveTelegram(workspace, { ...connection, enabled: true });
                  if (!live.current) return;
                  setConnection(null);
                  setChannels((old) => [...old, c]);
                  setDraft((old) =>
                    old
                      ? {
                          ...old,
                          destinations:
                            old.destinations.length < 10
                              ? [...old.destinations, { channelId: c.id, topicId: null }]
                              : old.destinations,
                        }
                      : old,
                  );
                })
              }
            >
              Сохранить подключение
            </Button>
          </EditorOnly>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="lg">
        <DialogTitle>Предпросмотр отчёта</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {preview && (
            <Box sx={{ p: 2 }}>
              <Typography fontWeight={600}>Текст для Telegram</Typography>
              <Typography
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13 }}
              >
                {preview.caption}
              </Typography>
              {preview.imageBase64 && (
                <Box
                  component="img"
                  alt="Карта спроса для Telegram"
                  src={`data:image/png;base64,${preview.imageBase64}`}
                  sx={{ display: 'block', width: '100%', maxWidth: 540, mx: 'auto' }}
                />
              )}
            </Box>
          )}
          {preview && (
            <iframe
              title="Предпросмотр HTML-отчёта"
              srcDoc={preview.html}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              style={{ width: '100%', height: '65vh', border: 0 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {preview?.imageBase64 && (
            <EditorOnly permissions="fakes.export">
              <Button onClick={() => download('png')}>Скачать PNG</Button>
            </EditorOnly>
          )}
          <EditorOnly permissions="fakes.export">
            <Button onClick={() => download('html')}>Скачать HTML</Button>
          </EditorOnly>
          <Button onClick={() => setPreview(null)}>Закрыть предпросмотр</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
